import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import MapView from './MapView';
import selectors from '../../modules/selectors/datePickerSelectors';
import {
  fetchCountyLevel,
  fetchShowrooms,
  fetchWonOpps,
  fetchZipcodeLevel,
  getPivotRange,
} from './services';
import SelectDropdown from '../../components/SelectDropdown';
import { colorPalette } from '../../data/colorPaletteForRange';
import { pivot } from '../../data/mapsData';
import { formatLongNumber } from '../../utils/formatUtils';
import CheckboxMui from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DownloadButton from '../../components/DownloadReport';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';

// Helper function to get toggle button style
const getToggleButtonStyle = (isActive, isDisabled) => {
  if (isDisabled) {
    return { height: '40px', color: '#a3a3a3' };
  }
  return {
    background: isActive ? 'black' : '#fafafa',
    color: isActive ? 'white' : 'black',
    borderColor: 'black',
    textTransform: 'capitalize',
    height: '40px',
  };
};

// Checkbox style configuration
const checkboxSx = {
  '& .MuiSvgIcon-root': { fontSize: 28 },
  '&.Mui-checked': { color: 'black' },
};

// Color legend item component
const ColorLegendItem = ({ color, label }) => (
  <div style={{ marginBottom: '3px', width: 'auto', display: 'flex', gap: '10px' }}>
    <div
      style={{
        background: color,
        height: '15px',
        width: '15px',
        border: '0.5px solid black',
      }}
    />
    {typeof label === 'string' ? <div>{label}</div> : label}
  </div>
);

ColorLegendItem.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

// Helper to process showrooms data
const processShowroomsData = (showroomsArray) => {
  if (!showroomsArray || !Array.isArray(showroomsArray)) return [];
  return showroomsArray.map((showroom) => ({
    name: showroom?.address,
    centre: [parseFloat(showroom?.lat), parseFloat(showroom?.lon)],
  }));
};

// Helper to configure pivot range from values
const configurePivotRange = (rangeValues) => {
  if (!rangeValues || rangeValues.length < 2) return [];
  return rangeValues.slice(0, -1).map((val, i) => [val, rangeValues[i + 1]]);
};

// Helper to normalize coordinate
const normalizeCoordinate = (coord) => {
  return coord?.[0] < 0 ? [coord[1], coord[0]] : coord;
};

// Helper to process polygon coordinates
const processPolygonCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates)) return [];
  return coordinates.map((coord) => {
    if (!Array.isArray(coord)) return [];
    return coord.map((item) => normalizeCoordinate(item));
  });
};

// Helper to get retail color for an item
const getItemRetailColor = (item, pivotValue) => {
  if (item.retail_status === 'Retail') {
    return colorPalette[pivotValue].colorPalette[0].color;
  }
  if (item.retail_status === 'Trade') {
    return colorPalette[pivotValue].colorPalette[1].color;
  }
  return 'white';
};

// Helper to get retail color for data array
const getRetailColor = (data, pivotValue) => {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item) => ({
    ...item,
    color: getItemRetailColor(item, pivotValue),
  }));
};

// Helper to configure county data from area
const configureCountyData = (area) => {
  if (!area) return undefined;
  if (area?.coordinates === null) return [area];

  const coordType = area?.coordinates?.type;

  if (coordType === 'Point') {
    return [{ ...area, polygonCoord: [normalizeCoordinate(area?.coordinates?.coordinates)] }];
  }

  if (coordType === 'Polygon' || coordType === 'MultiPolygon') {
    const coords = processPolygonCoordinates(area?.coordinates?.coordinates);
    return coords.map((coord) => ({ ...area, polygonCoord: coord }));
  }

  return undefined;
};

// Helper to build years array
const buildYearsArray = (currentYear, arrayMappingLength) => {
  const yearsArr = [];
  for (let i = 0; i < arrayMappingLength; i++) {
    yearsArr.push(currentYear - i);
  }
  return yearsArr.map((year) => ({ label: year, value: year }));
};

// Format range value with unit
const formatRangeValue = (value, unit) => {
  if (!unit) return formatLongNumber(value);
  if (unit === '$') {
    const formattedValue = formatLongNumber(Math.abs(value));
    return value < 0 ? `-${unit}${formattedValue}` : `${unit}${formattedValue}`;
  }
  return `${formatLongNumber(value)}${unit}`;
};

// Get range display element
const getRange = (index, unit, pivotRange, pivotValue) => {
  if (pivotRange?.length <= 2 || index < 0 || pivotValue === 'retail_status') {
    return <div></div>;
  }
  return (
    <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
      {formatRangeValue(pivotRange[index][0], unit)} -{' '}
      {formatRangeValue(pivotRange[index][1], unit)}
    </div>
  );
};

// Helper to render retail status legend
const renderRetailStatusLegend = (pivotValue, pivotRange) => {
  if (pivotValue !== 'retail_status') return null;
  return colorPalette[pivotValue].colorPalette.map((color, index) => (
    <ColorLegendItem
      key={`retail-status-${pivotRange[index]}`}
      color={color.color}
      label={pivotRange[index]}
    />
  ));
};

// Helper to render pivot range legend
const renderPivotRangeLegend = (pivotValue, isAllZeroPivotRange, pivotRange, pivot) => {
  if (pivotValue === 'retail_status') return null;

  if (isAllZeroPivotRange) {
    return (
      <ColorLegendItem
        color="white"
        label={
          <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>0.00 - 0.00</div>
        }
      />
    );
  }

  return colorPalette[pivotValue].colorPalette.map((color, index) => (
    <ColorLegendItem
      key={`pivot-range-${pivotRange[index]}`}
      color={color.color}
      label={getRange(
        index,
        pivot.find((piv) => piv.value === pivotValue)?.unit,
        pivotRange,
        pivotValue
      )}
    />
  ));
};

const MapPage = () => {
  const { calendarDt } = useSelector(selectors?.getCalendarYear);
  const { userData } = useSelector((state) => state?.auth);
  const currentYear = userData?.data?.current_year || calendarDt?.calendarYear?.currYear;
  const [showrooms, setShowrooms] = useState([]);
  const [showShowrooms, setShowShowrooms] = useState(false);
  const [showWonOpps, setShowWonOpps] = useState(false);
  const [countyLevel, setCountyLevel] = useState([]);
  const [wonOpps, setWonOpps] = useState([]);
  const [zipcodeLevel, setZipcodeLevel] = useState([]);
  const [view, setView] = useState('Zip Code');
  const [rangeValuesArray, setRangeValuesArray] = useState([]);
  const [pivotRange, setPivotRange] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const isCurrentYearGreater = calendarDt?.calendarYear?.currYear > userData?.data?.current_year;
  const arrayMappingLength = isCurrentYearGreater
    ? calendarDt?.processedDatesLength - 1
    : calendarDt?.processedDatesLength;
  const years = buildYearsArray(currentYear, arrayMappingLength);
  const [year, setYear] = useState(currentYear);
  const [pivotValue, setPivotValue] = useState(pivot[0].value);
  const [prevYear, setPrevYear] = useState(null);

  useEffect(() => {
    if (years.length) {
      setYear(years[0]?.value);
    }
  }, [calendarDt]);

  const {
    data: showroomsData,
    mutate: showroomsDataMutation,
    isSuccess: isSuccessShowroomsData,
    isLoading: isLoadingShowroomsData,
  } = useMutation({
    mutationFn: fetchShowrooms,
  });

  const {
    data: countyLevelData,
    mutate: countyLevelDataMutation,
    isSuccess: isSuccessCountyLevelData,
    isLoading: isLoadingCountyLevelData,
  } = useMutation({
    mutationFn: fetchCountyLevel,
  });

  const {
    data: zipcodeLevelData,
    mutate: zipcodeLevelDataMutation,
    isSuccess: isSuccessZipcodeLevelData,
    isLoading: isLoadingZipcodeLevelData,
  } = useMutation({
    mutationFn: fetchZipcodeLevel,
  });

  const {
    data: wonOppsData,
    mutate: wonOppsMutation,
    isSuccess: isSuccessWonOppsData,
    isLoading: isLoadingWonOppsData,
  } = useMutation({
    mutationFn: fetchWonOpps,
  });

  const {
    data: pivotRangeData,
    mutate: pivotRangeMutation,
    isSuccess: isSuccessPivotRangeData,
    isLoading: isLoadingPivotRangeData,
  } = useMutation({
    mutationFn: getPivotRange,
  });

  // Consolidated loading state check
  const isAnyLoading =
    isLoadingShowroomsData ||
    isLoadingCountyLevelData ||
    isLoadingWonOppsData ||
    isLoadingZipcodeLevelData ||
    isLoadingPivotRangeData ||
    isLoading;

  // Get color index based on value and pivot range
  const getColorIndex = (value) => {
    if (!value) return 0;
    const foundIndex = pivotRange?.findIndex((range) => value > range[0] && value <= range[1]);
    return foundIndex >= 0 ? foundIndex : 0;
  };

  // Apply color to data array based on pivot value
  const getAreaColor = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      ...item,
      color: colorPalette?.[pivotValue]?.colorPalette[getColorIndex(item?.[pivotValue])]?.color,
    }));
  };

  const handleYearChange = (currentYear) => {
    setPrevYear(year);
    setYear(currentYear);
    setShowShowrooms(false);
    setShowWonOpps(false);
  };

  const handleView = () => {
    setView((prevView) => (prevView === 'Zip Code' ? 'County' : 'Zip Code'));
    setShowShowrooms(false);
    setShowWonOpps(false);
  };

  const handlePivotValue = (value) => {
    setPivotValue(value);
    setShowShowrooms(false);
    setShowWonOpps(false);
    setPivotRange([]);
  };

  // Check if pivot range has all zeros
  const isAllZeroPivotRange =
    pivotValue !== 'retail_status' && rangeValuesArray?.every((piv) => piv === 0);

  useEffect(() => {
    if (prevYear === year || !year) return;
    setIsLoading(true);
    if (view === 'Zip Code') {
      setZipcodeLevel([]);
      zipcodeLevelDataMutation(year);
    } else {
      setCountyLevel([]);
      countyLevelDataMutation(year);
    }
  }, [year, view]);

  useEffect(() => {
    showroomsDataMutation();
    year && wonOppsMutation(year);
  }, [year]);

  useEffect(() => {
    if (pivotValue === 'retail_status') {
      setPivotRange(['Retail', 'Trade']);
    } else {
      year &&
        pivotRangeMutation({
          year,
          pivotValue,
          level: view === 'Zip Code' ? 'zip' : 'county',
        });
    }
  }, [year, pivotValue, view]);

  useEffect(() => {
    if (isSuccessPivotRangeData) {
      const rangeValues = pivotRangeData?.data;
      const rangeArray = configurePivotRange(rangeValues);
      setRangeValuesArray(rangeValues);
      setPivotRange(rangeArray);
    }
  }, [isSuccessPivotRangeData]);

  useEffect(() => {
    if (isSuccessShowroomsData) {
      setShowrooms(processShowroomsData(showroomsData?.data));
    }
  }, [isSuccessShowroomsData, showroomsData]);

  useEffect(() => {
    if (isSuccessCountyLevelData) {
      const dataArray = getAreaColor(countyLevelData?.data);
      const countyData = dataArray
        .map((item) => configureCountyData(item))
        .flatMap((el) => (Array.isArray(el) ? el : [el]));
      setCountyLevel(countyData);
    }
  }, [isSuccessCountyLevelData, countyLevelData, pivotValue, pivot, rangeValuesArray]);

  useEffect(() => {
    if (isSuccessZipcodeLevelData && pivotValue) {
      const data = zipcodeLevelData?.data;
      const dataArray =
        pivotValue === 'retail_status' ? getRetailColor(data, pivotValue) : getAreaColor(data);
      setZipcodeLevel(dataArray);
    }
  }, [isSuccessZipcodeLevelData, zipcodeLevelData, pivotValue, year, rangeValuesArray]);

  useEffect(() => {
    if (isSuccessWonOppsData) {
      let data = wonOppsData?.data;
      setWonOpps(data);
    }
  }, [isSuccessWonOppsData, wonOppsData]);

  // Helper to check if data is ready for the current view
  const isDataReadyForView = (viewData) => {
    if (!showrooms?.length || !viewData?.length) return false;
    if (pivotValue !== 'retail_status') {
      return pivotRange?.length > 0;
    }
    return true;
  };

  useEffect(() => {
    const viewData = view === 'County' ? countyLevel : zipcodeLevel;
    if (isDataReadyForView(viewData)) {
      setIsLoading(false);
    }
  }, [showrooms, countyLevel, zipcodeLevel, pivotRange, view]);

  return (
    <>
      {currentYear ? (
        <div style={{ padding: 10, width: 'auto', height: '100%' }}>
          <div style={{ display: 'flex', height: '85vh' }}>
            <MapView
              data={showrooms}
              isLoading={isAnyLoading}
              showShowrooms={showShowrooms}
              countyAreas={countyLevel}
              view={view}
              zipcodeAreas={zipcodeLevel}
              wonOppsData={wonOpps}
              showWonOpps={showWonOpps}
            />
            <div
              style={{
                padding: '0px 5px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                backgroundColor: 'white',
                width: '240px',
              }}
            >
              <div style={{ backgroundColor: 'white', padding: '5px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ margin: '24px 0 0 7px' }}>
                    <ToggleButtonGroup
                      disabled={isAnyLoading}
                      value={'Zip Code'}
                      onChange={handleView}
                      exclusive
                      aria-label="Toggle Zip code And County View"
                      sx={{ paddingRight: '20px' }}
                    >
                      <ToggleButton
                        value={'Zip Code'}
                        style={getToggleButtonStyle(view === 'Zip Code', isAnyLoading)}
                      >
                        Zip Code
                      </ToggleButton>
                      <ToggleButton
                        value={'County'}
                        style={getToggleButtonStyle(view === 'County', isAnyLoading)}
                      >
                        County
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                </div>
                <div>
                  <SelectDropdown
                    disabled={isAnyLoading}
                    width={200}
                    options={years}
                    singleSelectDefaultValue={userData?.data?.current_year || years[0]?.value}
                    label={'Fiscal Year'}
                    handleFilterChange={handleYearChange}
                  />
                </div>
                <div>
                  <SelectDropdown
                    disabled={isAnyLoading}
                    width={200}
                    options={
                      view === 'Zip Code'
                        ? pivot
                        : pivot.filter((piv) => piv.value !== 'retail_status')
                    }
                    singleSelectDefaultValue={pivot[0].value}
                    label={'Pivot'}
                    handleFilterChange={handlePivotValue}
                  />
                </div>
                <div style={{ paddingLeft: '8px' }}>
                  <FormControlLabel
                    onChange={(e) => setShowWonOpps(e.target.checked)}
                    control={
                      <CheckboxMui
                        disabled={isAnyLoading}
                        defaultChecked={showWonOpps}
                        value={showWonOpps}
                        checked={showWonOpps}
                        sx={checkboxSx}
                      />
                    }
                    label={'Show Won Opps'}
                  />
                </div>
                <div style={{ paddingLeft: '8px' }}>
                  <FormControlLabel
                    onChange={(e) => setShowShowrooms(e.target.checked)}
                    control={
                      <CheckboxMui
                        disabled={isAnyLoading}
                        defaultChecked={showShowrooms}
                        value={showShowrooms}
                        checked={showShowrooms}
                        sx={checkboxSx}
                      />
                    }
                    label={'Show Showrooms'}
                  />
                </div>
                {view === 'Zip Code' && (
                  <Tooltip title={`Download report for ${years[0]?.value}`} placement="bottom">
                    <div style={{ paddingLeft: '8px' }}>
                      <Tooltip title={`Download report for ${years[0]?.value}`} placement="bottom">
                        <DownloadButton
                          filename="map_view_zip_data.csv"
                          setIsDownloading={setIsDownloading}
                          isDownloading={isDownloading}
                          year={years[0]?.value}
                          isDisabled={isLoading}
                          mapDownload={true}
                          showWonOpps={showWonOpps}
                          fileType="csv"
                        />
                      </Tooltip>
                    </div>
                  </Tooltip>
                )}
              </div>
              <div style={{ backgroundColor: 'white', padding: '5px' }}>
                {pivotValue && (
                  <h4
                    style={{
                      fontSize: '1rem',
                      fontWeight: 400,
                      lineHeight: 1.5,
                      margin: '3px 0',
                    }}
                  >
                    {pivot.find((piv) => piv.value === pivotValue)?.label}
                  </h4>
                )}
                <div style={{ marginBottom: '25px' }}>
                  {isLoadingPivotRangeData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={35} thickness={3.7} disableShrink />
                    </Box>
                  ) : (
                    <>
                      {pivotValue && renderRetailStatusLegend(pivotValue, pivotRange)}
                      {pivotValue &&
                        renderPivotRangeLegend(pivotValue, isAllZeroPivotRange, pivotRange, pivot)}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margin: 'auto',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      )}
    </>
  );
};

export default MapPage;
